import React, { useState } from 'react';
import { School, BookText, GraduationCap, ChevronRight } from 'lucide-react';

interface StageProps {
  icon: React.ElementType;
  title: string;
  description: string;
  bgColor: string;
  iconBg: string;
  isActive: boolean;
  onClick: () => void;
}

const Stage: React.FC<StageProps> = ({ 
  icon: Icon, 
  title, 
  description, 
  bgColor, 
  iconBg,
  isActive,
  onClick
}) => {
  return (
    <div 
      className={`cursor-pointer rounded-xl p-8 transition-all duration-300 transform ${
        isActive 
          ? `${bgColor} text-white shadow-2xl scale-105` 
          : 'bg-white border-2 border-gray-200 hover:border-blue-300 hover:shadow-lg hover:-translate-y-1'
      }`}
      onClick={onClick}
    >
      <div className="flex items-center mb-6">
        <div className={`${iconBg} rounded-xl p-4 ml-4 ${isActive ? 'bg-white/20' : ''}`}>
          <Icon className={`h-8 w-8 ${isActive ? 'text-white' : 'text-white'}`} />
        </div>
        <h3 className={`text-2xl font-bold ${isActive ? 'text-white' : 'text-gray-800'}`}>
          {title}
        </h3>
      </div>
      <p className={`text-lg leading-relaxed ${isActive ? 'text-white/90' : 'text-gray-600'}`}>
        {description}
      </p>
      {!isActive && (
        <div className="mt-4 flex items-center text-blue-600">
          <span className="text-sm font-medium">اعرف المزيد</span>
          <ChevronRight className="h-4 w-4 mr-2" />
        </div>
      )}
    </div>
  );
};

const EducationalStages = () => {
  const [activeStage, setActiveStage] = useState(0);

  const stages = [
    {
      icon: School,
      title: "المرحلة الابتدائية",
      description: "بناء أساس قوي للمتعلمين الصغار في المواد الأساسية وتطوير مهارات التعلم الضرورية للنجاح المستقبلي.",
      bgColor: "bg-gradient-to-br from-blue-700 to-blue-800",
      iconBg: "bg-blue-600",
      subjects: ["الرياضيات", "العلوم", "اللغة العربية", "اللغة الإنجليزية", "التربية الإسلامية"]
    },
    {
      icon: BookText,
      title: "المرحلة المتوسطة",
      description: "دعم الطلاب خلال سنوات نموهم الحرجة مع تغطية شاملة للمواد والتحضير المتقدم للاختبارات.",
      bgColor: "bg-gradient-to-br from-teal-600 to-teal-700",
      iconBg: "bg-teal-500",
      subjects: ["الرياضيات", "العلوم", "اللغة العربية", "اللغة الإنجليزية", "الحاسب الآلي", "الدراسات الاجتماعية"]
    },
    {
      icon: GraduationCap,
      title: "المرحلة الثانوية",
      description: "إعداد شامل للطلاب للتعليم العالي مع إتقان المواد المتقدمة والتحضير المكثف للاختبارات العامة.",
      bgColor: "bg-gradient-to-br from-amber-500 to-amber-600",
      iconBg: "bg-amber-400",
      subjects: ["الرياضيات المتقدمة", "الفيزياء", "الكيمياء", "الأحياء", "اللغة الإنجليزية", "الحاسب الآلي"]
    }
  ];

  return (
    <section id="stages" className="py-20 md:py-28 bg-white">
      <div className="container mx-auto px-4">
        {/* العنوان الرئيسي */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900">
            المراحل التعليمية التي نغطيها
          </h2>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            نقدم خدماتنا للطلاب في كل مرحلة من مراحل رحلتهم الأكاديمية، مع تقديم دعم مخصص ومتخصص لكل مستوى تعليمي.
          </p>
        </div>
        
        {/* بطاقات المراحل */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
          {stages.map((stage, index) => (
            <Stage
              key={index}
              icon={stage.icon}
              title={stage.title}
              description={stage.description}
              bgColor={stage.bgColor}
              iconBg={stage.iconBg}
              isActive={activeStage === index}
              onClick={() => setActiveStage(index)}
            />
          ))}
        </div>
        
        {/* تفاصيل المرحلة المختارة */}
        <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-2xl shadow-xl p-10 border border-gray-200">
          <div className="flex flex-col lg:flex-row items-center gap-12">
            <div className="w-full lg:w-2/3">
              <h3 className="text-3xl font-bold mb-6 text-gray-800">
                تعليم مخصص لطلاب {stages[activeStage].title}
              </h3>
              <p className="text-gray-600 mb-8 text-lg leading-relaxed">
                يقدم معلمونا المتخصصون اهتماماً شخصياً وتوجيهاً منظماً لمساعدة الطلاب في {stages[activeStage].title} على التفوق في دراستهم. نحن نفهم التحديات المحددة في هذه المرحلة ونقدم الدعم المستهدف لمواجهتها بنجاح.
              </p>
              
              <div className="mb-8">
                <h4 className="text-xl font-semibold mb-4 text-gray-800">المواد التي نغطيها:</h4>
                <div className="flex flex-wrap gap-3">
                  {stages[activeStage].subjects.map((subject, index) => {
                    const colors = [
                      'bg-blue-100 text-blue-800',
                      'bg-teal-100 text-teal-800', 
                      'bg-amber-100 text-amber-800'
                    ];
                    return (
                      <span 
                        key={index}
                        className={`${colors[activeStage]} px-4 py-2 rounded-full text-sm font-medium`}
                      >
                        {subject}
                      </span>
                    );
                  })}
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <a 
                  href="https://wa.me/+9660590045721"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-8 py-4 bg-blue-800 hover:bg-blue-900 text-white font-bold rounded-xl transition-all duration-300 transform hover:scale-105 text-center"
                >
                  احجز جلستك الأولى
                </a>
                <button 
                  onClick={() => {
                    const element = document.getElementById('pricing');
                    if (element) element.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="px-8 py-4 bg-transparent border-2 border-blue-800 text-blue-800 hover:bg-blue-800 hover:text-white font-bold rounded-xl transition-all duration-300 text-center"
                >
                  اطلع على الأسعار
                </button>
              </div>
            </div>
            
            <div className="w-full lg:w-1/3 flex justify-center">
              <div className="relative">
                <div className={`rounded-full p-6 ${stages[activeStage].iconBg} bg-opacity-10`}>
                  <div className={`rounded-full p-8 ${stages[activeStage].iconBg} bg-opacity-20`}>
                    <div className={`${stages[activeStage].iconBg} rounded-full p-8`}>
                      {React.createElement(stages[activeStage].icon, { 
                        className: "h-16 w-16 text-white" 
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default EducationalStages;